import { ColumnOrderState, ColumnSizingState, VisibilityState } from '@tanstack/react-table';
import { snakeCase } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import { readLocalStorage, removeLocalStorageItem, writeLocalStorage } from '../../utils/localStorage';

interface UseTableColumnSettingsOptions {
    defaultColumnVisibility?: VisibilityState;
    defaultColumnOrder?: ColumnOrderState;
}

export const useTableColumnSettings = (tableId: string, options?: UseTableColumnSettingsOptions) => {
    // Memoize default values to prevent new references on every render
    const defaultColumnVisibility = useMemo(
        () => options?.defaultColumnVisibility || {},
        [options?.defaultColumnVisibility],
    );
    const defaultColumnOrder = useMemo(() => options?.defaultColumnOrder || [], [options?.defaultColumnOrder]);

    const { colVisibilityKey, colOrderKey, colSizingKey } = useMemo(() => {
        const TABLE_ID = snakeCase(tableId).toUpperCase();

        return {
            colVisibilityKey: `LS_KEY_${TABLE_ID}_TABLE_COL_VISIBILITY`,
            colOrderKey: `LS_KEY_${TABLE_ID}_TABLE_COL_ORDER`,
            colSizingKey: `LS_KEY_${TABLE_ID}_TABLE_COL_SIZING`,
        };
    }, [tableId]);

    const [columnVisibility, setColumnVisibility] = useState(() => {
        const value = readLocalStorage(colVisibilityKey, defaultColumnVisibility);
        return value;
    });
    const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() => {
        const value = readLocalStorage<ColumnOrderState>(colOrderKey, defaultColumnOrder);
        return value;
    });
    const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() => {
        const value = readLocalStorage<ColumnSizingState>(colSizingKey, {});
        return value;
    });

    useEffect(() => {
        setColumnVisibility(readLocalStorage(colVisibilityKey, defaultColumnVisibility));
    }, [colVisibilityKey, defaultColumnVisibility]);

    useEffect(() => {
        setColumnOrder(readLocalStorage<ColumnOrderState>(colOrderKey, defaultColumnOrder));
    }, [colOrderKey, defaultColumnOrder]);

    useEffect(() => {
        setColumnSizing(readLocalStorage<ColumnSizingState>(colSizingKey, {}));
    }, [colSizingKey]);

    useEffect(() => {
        if (columnVisibility) {
            writeLocalStorage(colVisibilityKey, columnVisibility);
        }
    }, [colVisibilityKey, columnVisibility]);

    useEffect(() => {
        if (columnOrder) {
            writeLocalStorage(colOrderKey, columnOrder);
        }
    }, [colOrderKey, columnOrder]);

    useEffect(() => {
        if (columnSizing && Object.keys(columnSizing).length > 0) {
            writeLocalStorage(colSizingKey, columnSizing);
        }
    }, [colSizingKey, columnSizing]);

    const resetSettings = () => {
        setColumnVisibility(defaultColumnVisibility);
        setColumnOrder(defaultColumnOrder);
        setColumnSizing({});
        removeLocalStorageItem(colVisibilityKey);
        removeLocalStorageItem(colOrderKey);
        removeLocalStorageItem(colSizingKey);
    };

    return {
        columnVisibility,
        setColumnVisibility,
        columnOrder,
        setColumnOrder,
        columnSizing,
        setColumnSizing,
        resetSettings,
    };
};
